import moment from 'moment-timezone';
import { FONT_FAMILY , TOOLTIP_LABEL_FORMAT } from '@utils/constants';
import { humanize } from '@utils';
import { getMetricUnitByKey, getMetricUnitText } from '@utils/metrics';
import { DEFAULT_FILTER_COLOR } from '@src/utils/colorPalette';
import { updateChartWithDodSeries } from './dodSeries';
import type { MetricsMetaTypes } from '@pulse/metrics';

export const formatMetricDataForChart = ({
  chartData,
  metricKey,
  color = DEFAULT_FILTER_COLOR,
  dodData = undefined,
}: {
  chartData: any;
  metricKey: any;
  color?: string;
  dodData?: any;
}) => {
  const tooltipFormatter = (params) => {
    const unit = getMetricUnitByKey({ metricKey });

    let xVal = '';
    let currentContent = '';
    let dodContent = '';

    params.forEach((param) => {
      const value = param.data;
      const yVal = humanize(value[1], unit);
      const marker = `<span style="display:inline-block;background-color:${param.color};width:7px;height:14px;position:relative;top:2px;margin-right:4px;"></span>`;

      if (param.seriesId === 'dod-series') {
        const dodDate = value[2]
          ? moment(value[2]).format(TOOLTIP_LABEL_FORMAT)
          : moment(value[0]).format(TOOLTIP_LABEL_FORMAT);
        dodContent = `${dodDate}<br/>${marker}<span style="margin-right:10px;">${yVal}${getMetricUnitText(unit)}</span><br/>`;
      } else {
        xVal = moment(value[0]).format(TOOLTIP_LABEL_FORMAT);
        currentContent = `${marker}<span style="margin-right:10px;">${yVal}${getMetricUnitText(unit)}</span><br/>`;
      }
    });

    return `${xVal}<br/>${currentContent}${dodContent}`;
  };

  // Base chart option with primary series
  let option: any = {
    _viewType: 'timeseries',
    color: [color],
    grid: {
      left: 0,
      bottom: 0,
      right: 0,
      top: 5,
    },
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      font: FONT_FAMILY,
      appendToBody: true,
      className: 'echarts-tooltip',
    },
    xAxis: {
      type: 'time',
      show: false,
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        id: 'primary-series',
        name: 'Current',
        data: chartData,
        type: 'line',
        showSymbol: false,
        areaStyle: {
          opacity: 0.1,
          color,
        },
        lineStyle: {
          width: 2,
        },
        z: 2, // Higher z-index for primary series
      },
    ],
  };

  // Add DoD series with overlapping if available
  if (dodData && dodData.length > 0) {
    option = updateChartWithDodSeries(option, dodData, false, chartData);
    option.color = ['#d9d9d9', color];
  }

  return option;
};

// TODO: refactor as part of humanize cleanup
export const getHumanizedValueString = (data, metricKey) => {
  const value = humanize(data[metricKey] || 0, '');
  const unit = getMetricUnitByKey({ metricKey });
  if (unit) {
    const units = getMetricUnitText(unit);
    return `${value}${units}`;
  } else {
    return value;
  }
};

/**
 * Determines if a percentage change is good or bad based on the metric's reverse property
 *
 * @param percentageChange - The percentage change value (positive for increase, negative for decrease)
 * @param metricMeta - The metric metadata containing the reverse property
 * @returns true if the change is good/favorable, false if bad/unfavorable, null if unable to determine
 *
 * For metrics with reverse=true (like error rates), higher values are worse:
 * - Increase (positive change) = bad = false (red)
 * - Decrease (negative change) = good = true (green)
 *
 * For metrics with reverse=false/absent (like bitrate), higher values are better:
 * - Increase (positive change) = good = true (green)
 * - Decrease (negative change) = bad = false (red)
 */
export const isMetricChangeGood = (
  percentageChange: number | null | undefined,
  metricMeta?: Partial<MetricsMetaTypes>,
): boolean | null => {
  if (percentageChange === null || percentageChange === undefined) {
    return null;
  }

  // For reverse metrics (higher is bad), decrease is good
  // For normal metrics (higher is good), increase is good
  return metricMeta?.reverse ? percentageChange < 0 : percentageChange > 0;
};
